import React, { useCallback, useMemo, useState } from "react";
import { ScrollText, RefreshCw, Loader2 } from "lucide-react";
import { api } from "../../lib/api";

/**
 * System Logs — read-only tail of a whitelisted supervisor log file.
 *
 * Diagnostic utility.  Admin-auth is enforced by the backend; this
 * component only shells over to `GET /admin/system/logs`.  No polling,
 * no streaming — manual refresh only.
 */
export default function SystemLogsSection() {
    const [source, setSource] = useState("backend_memory");
    const [lines, setLines] = useState(500);
    const [filter, setFilter] = useState("");
    const [raw, setRaw] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [lastFetchedAt, setLastFetchedAt] = useState(null);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        setErr("");
        try {
            const r = await api.get("/admin/system/logs", {
                params: {
                    source,
                    lines: Math.max(1, Math.min(2000, Number(lines) || 500)),
                },
                // The endpoint returns text/plain; axios decodes as string.
                responseType: "text",
                transformResponse: [(d) => d],
            });
            setRaw(typeof r.data === "string" ? r.data : String(r.data ?? ""));
            setLastFetchedAt(new Date());
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Failed to load logs.");
            setRaw("");
        } finally {
            setLoading(false);
        }
    }, [source, lines]);

    const filtered = useMemo(() => {
        if (!raw) return "";
        const q = filter.trim().toLowerCase();
        if (!q) return raw;
        return raw
            .split("\n")
            .filter((ln) => ln.toLowerCase().includes(q))
            .join("\n");
    }, [raw, filter]);

    const visibleCount = filtered ? filtered.split("\n").length : 0;

    return (
        <section
            className="rounded-lg border border-[#E5E2DC] bg-white p-5"
            data-testid="system-logs-section">
            <div className="flex items-center gap-2 mb-3">
                <ScrollText size={16} className="text-charcoal" />
                <h2 className="font-manrope font-semibold text-[15px] text-charcoal">
                    System Logs
                </h2>
            </div>
            <p className="text-[12px] text-[#666] leading-snug mb-4">
                Tail the last N lines of a supervisor-managed backend log.
                Read-only. Manual refresh. Client-side filter greps the
                already-loaded buffer (useful for narrowing to{" "}
                <code className="font-mono text-[11px]">[timing]</code>).
            </p>

            <div className="grid grid-cols-1 md:grid-cols-[160px_120px_1fr_auto] gap-2 items-end mb-3">
                <label className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase tracking-wide text-[#888]">Source</span>
                    <select
                        value={source}
                        onChange={(e) => setSource(e.target.value)}
                        data-testid="system-logs-source"
                        className="h-9 rounded-md border border-[#E5E2DC] bg-white px-2 text-[13px]">
                        <option value="backend_memory">backend (in-process buffer)</option>
                        <option value="backend_err">backend.err.log</option>
                        <option value="backend_out">backend.out.log</option>
                    </select>
                </label>
                <label className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase tracking-wide text-[#888]">Lines</span>
                    <input
                        type="number"
                        min={1}
                        max={2000}
                        value={lines}
                        onChange={(e) => setLines(e.target.value)}
                        data-testid="system-logs-lines"
                        className="h-9 rounded-md border border-[#E5E2DC] bg-white px-2 text-[13px]" />
                </label>
                <label className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase tracking-wide text-[#888]">Filter (client-side, case-insensitive)</span>
                    <input
                        type="text"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        placeholder="e.g. [timing]"
                        data-testid="system-logs-filter"
                        className="h-9 rounded-md border border-[#E5E2DC] bg-white px-2 text-[13px] font-mono" />
                </label>
                <button
                    type="button"
                    onClick={fetchLogs}
                    disabled={loading}
                    data-testid="system-logs-refresh"
                    className="h-9 inline-flex items-center justify-center gap-1.5 rounded-md bg-charcoal px-3 text-[13px] text-[#F5F3EE] disabled:opacity-60">
                    {loading
                        ? (<Loader2 size={14} className="animate-spin" />)
                        : (<RefreshCw size={14} />)}
                    Refresh
                </button>
            </div>

            {err && (
                <div
                    data-testid="system-logs-error"
                    className="mb-2 text-[12px] text-terracotta">
                    {err}
                </div>
            )}

            <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] text-[#888]" data-testid="system-logs-meta">
                    {lastFetchedAt
                        ? `Loaded ${lastFetchedAt.toLocaleTimeString("en-GB")} · ${visibleCount} line${visibleCount === 1 ? "" : "s"} shown`
                        : "Not yet loaded"}
                </span>
                {filter && raw && (
                    <span className="text-[11px] text-[#888]">
                        Filtered from {raw.split("\n").length} total
                    </span>
                )}
            </div>

            <pre
                data-testid="system-logs-output"
                className="h-72 max-h-[28rem] overflow-auto rounded-md border border-[#E5E2DC] bg-[#FAF8F4] p-3 text-[12px] leading-[1.5] font-mono text-charcoal whitespace-pre">
                {filtered || (lastFetchedAt ? "(no matching lines)" : "")}
            </pre>
        </section>
    );
}
